import { ReactElement } from 'react'

import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Alert } from 'antd'
import { Translation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { IconButton } from 'components/Buttons'
import { CloseIcon } from 'components/Icons'
import { TextButton } from 'components/TextButton'
import { useOrgContext } from 'context/OrgContext'
import { userHasDashboardsOnlyPermissions } from 'helpers/featurePermissions'
import { useUserHasPermissions } from 'hooks'
import usePersistedDismiss from 'hooks/usePersistedDismiss'
import { getPath } from 'routing/pagePaths'

import * as styles from './styles.scss'

const MAP_INTEGRATION_KEY_TO_SUB_PATH = {
    seller_central: '/amazon-integrations?tab=selling-partner',
    vendor_central: '/amazon-integrations?tab=vendor-central',
    amazon_advertising: '/amazon-integrations?tab=advertising',
    amazon_advertising_dsp: '/amazon-integrations?tab=advertising',
}

export default function IntegrationIssuesBanner(): ReactElement | null {
    const dashboardOnlyExperience = useUserHasPermissions(
        userHasDashboardsOnlyPermissions
    )

    const orgContext = useOrgContext()
    const navigate = useNavigate()
    const location = useLocation()
    const { dismiss, isDismissed } = usePersistedDismiss(
        'integrationIssuesBanner',
        1
    )

    if (
        dashboardOnlyExperience ||
        !orgContext?.orgAccountStatus?.integration_issues?.length ||
        location.pathname === '/account' ||
        location.pathname.includes('/print')
    ) {
        return null
    }

    const distinctIntegrationsWithIssues = new Set<number>()
    let subItem: string | undefined =
        MAP_INTEGRATION_KEY_TO_SUB_PATH[
            orgContext.orgAccountStatus.integration_issues[0].integration_key
        ]

    orgContext.orgAccountStatus.integration_issues.forEach((issue) => {
        if (
            MAP_INTEGRATION_KEY_TO_SUB_PATH[issue.integration_key] !== subItem
        ) {
            subItem = undefined
        }
        distinctIntegrationsWithIssues.add(issue.integration_id)
    })
    const pathToIntegrationsWithIssues = `${getPath('settingsPage')}${subItem ?? ''}`

    if (isDismissed) {
        return null
    }

    return (
        <Alert
            type="warning"
            banner
            showIcon={false}
            message={
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <ExclamationCircleOutlined
                            className={styles['warning-icon']}
                        />
                        <span>
                            <Translation>
                                {(t) =>
                                    t(
                                        'account:Banners.integrationIssuesBanner.message',
                                        '{{count}} integration requires attention.',
                                        {
                                            count: distinctIntegrationsWithIssues.size,
                                        }
                                    )
                                }
                            </Translation>{' '}
                            <TextButton
                                link
                                onClick={() =>
                                    navigate(pathToIntegrationsWithIssues)
                                }
                            >
                                <Translation>
                                    {(t) =>
                                        t(
                                            'account:Banners.integrationIssuesBanner.cta',
                                            'Fix integrations'
                                        )
                                    }
                                </Translation>
                            </TextButton>
                        </span>
                    </div>
                    <IconButton
                        icon={<CloseIcon />}
                        onClick={dismiss}
                        size="small"
                        type="link"
                    />
                </div>
            }
            className={styles['warning-banner']}
        />
    )
}
